/**
 * Navbar Switcher - Switches between Desktop and Mobile Navbar
 * Supports forced view mode via URL parameter (?view=desktop or ?view=mobile)
 * Includes scroll hide/show behavior
 */
import { useEffect, useState } from 'react'
import NavbarDesktop from './NavbarDesktop'
import NavbarMobile from './NavbarMobile'

export default function Navbar() {
  const [viewMode, setViewMode] = useState<'desktop' | 'mobile' | 'auto'>('auto')
  const [isPortrait, setIsPortrait] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)

  useEffect(() => {
    setMounted(true)

    // Check for forced view mode via URL parameter (?view=desktop or ?view=mobile)
    const urlParams = new URLSearchParams(window.location.search)
    const forcedView = urlParams.get('view')
    if (forcedView === 'desktop' || forcedView === 'mobile') {
      setViewMode(forcedView)
      return // Don't set up media query listener if view is forced
    }

    // Auto mode: use media query
    const mq = window.matchMedia('(orientation: portrait)')
    setIsPortrait(mq.matches)
    const handler = (e: MediaQueryListEvent) => setIsPortrait(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // Scroll hide/show logic
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      // Always show navbar at the top of the page
      if (currentScrollY < 100) {
        setIsVisible(true)
        setLastScrollY(currentScrollY)
        return
      }

      // Scrolling down = hide, scrolling up = show
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false)
      } else if (currentScrollY < lastScrollY) {
        setIsVisible(true)
      }

      setLastScrollY(currentScrollY)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [lastScrollY])

  // Determine which view to show
  const showMobile = viewMode === 'mobile' || (viewMode === 'auto' && isPortrait)

  // SSR: Default to desktop
  if (!mounted) return <NavbarDesktop isVisible={true} />

  // Client: Switch based on view mode
  return showMobile ? <NavbarMobile isVisible={isVisible} /> : <NavbarDesktop isVisible={isVisible} />
}
